import React, { createContext, useContext } from "react";
import "./skeleton.scss";

type Size = number | string;

// Fixed width cycle (percent) — never Math.random(), or bars reshuffle on every render.
const WIDTHS = [72, 48, 86, 56, 64, 40, 78, 52];
const cycle = (i: number) => WIDTHS[i % WIDTHS.length];
const range = (n: number) => Array.from({ length: Math.max(0, n) }, (_, i) => i);

// ─── Building block ───────────────────────────────────────────────────────────
interface SkeletonProps {
  width?: Size;
  height?: Size;
  radius?: Size;
  circle?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const Skeleton = ({
  width = "100%",
  height = 12,
  radius = 6,
  circle = false,
  className,
  style,
}: SkeletonProps) => (
  <span
    aria-hidden="true"
    className={className ? `wmx-skeleton ${className}` : "wmx-skeleton"}
    style={{
      width: circle ? height : width,
      height,
      borderRadius: circle ? "50%" : radius,
      ...style,
    }}
  />
);

// ─── Status region ────────────────────────────────────────────────────────────
// Only the outermost composite announces itself, so a PageSkeleton holding a
// chart and a table is one "Loading…" for a screen reader, not three.
// Exported for a site that composes its own layout from bare Skeletons (a
// drawer body, say): wrap the whole of it so it is still one region.
const NestedContext = createContext(false);

interface RegionProps {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export const SkeletonRegion = ({ className, style, children }: RegionProps) => {
  const nested = useContext(NestedContext);
  if (nested) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }
  return (
    <NestedContext.Provider value={true}>
      <div role="status" aria-busy="true" className={className} style={style}>
        <span className="visually-hidden">Loading…</span>
        {children}
      </div>
    </NestedContext.Provider>
  );
};

// ─── Table ────────────────────────────────────────────────────────────────────
// The filter-bar strip drawn when the loader it replaces also hid FilterMain.
const ToolbarSkeleton = () => (
  <div className="d-flex align-items-center gap-2 mb-3">
    <Skeleton width={120} height={30} radius={15} />
    <Skeleton width={96} height={30} radius={15} />
    <Skeleton width={140} height={30} className="ms-auto" />
  </div>
);

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  toolbar?: boolean;
  header?: boolean;
  minHeight?: Size;
  className?: string;
}

export const TableSkeleton = ({
  rows = 10,
  columns = 6,
  toolbar = false,
  header = true,
  minHeight = 465,
  className,
}: TableSkeletonProps) => (
  <SkeletonRegion
    className={`table-box wmx-skeleton-table${className ? ` ${className}` : ""}`}
    style={{ minHeight }}
  >
    {toolbar && <ToolbarSkeleton />}
    <table className="wmx-table-style table mb-0">
      {header && (
        <thead>
          <tr>
            {range(columns).map((c) => (
              <th key={c}>
                <Skeleton width={`${cycle(c + 3)}%`} height={10} />
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {range(rows).map((r) => (
          <tr key={r}>
            {range(columns).map((c) => (
              <td key={c}>
                <Skeleton width={`${cycle(r * 3 + c)}%`} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </SkeletonRegion>
);

// ─── Chart ────────────────────────────────────────────────────────────────────
// Mirrors LineChartHighChart: "Charts" title + metric select, then the plot.
// header={false} draws the plot area only, for a site that keeps its own card.
interface ChartSkeletonProps {
  height?: number;
  header?: boolean;
}

export const ChartSkeleton = ({ height = 400, header = true }: ChartSkeletonProps) =>
  header ? (
    <SkeletonRegion className="overview-card">
      <div className="card-body">
        <div className="d-flex align-items-center mb-3">
          <Skeleton width={64} height={20} />
          <Skeleton width={96} height={31} className="ms-auto" />
        </div>
        <Skeleton height={height} radius={8} />
      </div>
    </SkeletonRegion>
  ) : (
    <SkeletonRegion>
      <Skeleton height={height} radius={8} />
    </SkeletonRegion>
  );

// ─── Card grid ────────────────────────────────────────────────────────────────
// Each card is the dashboard's SkeletonCard: label, value, sub-line.
const CARD_LINES = [
  { height: 12, width: 50 },
  { height: 28, width: 68 },
  { height: 11, width: 38 },
];

interface CardGridSkeletonProps {
  count?: number;
  colClassName?: string;
  lines?: number;
}

export const CardGridSkeleton = ({
  count = 4,
  colClassName = "col-md-6 col-lg-3",
  lines = 3,
}: CardGridSkeletonProps) => (
  <SkeletonRegion className="row g-3">
    {range(count).map((i) => (
      <div key={i} className={colClassName}>
        <div className="wmx-skeleton-card">
          <div className="p-3">
            {range(lines).map((l) => {
              const line = CARD_LINES[l] || { height: 11, width: cycle(i * 3 + l) };
              return (
                <Skeleton
                  key={l}
                  height={line.height}
                  width={`${line.width}%`}
                  className={l < lines - 1 ? "mb-2" : undefined}
                />
              );
            })}
          </div>
        </div>
      </div>
    ))}
  </SkeletonRegion>
);

// ─── Pick-list ────────────────────────────────────────────────────────────────
// lines={1} for rows that are one label (a filter modal's checkbox list), so
// the skeleton does not promise a sub-line that never arrives.
// flush drops the rows' side padding, for a list whose container already pads
// it and whose real rows sit flush (the SB/SP targeting panes).
interface ListSkeletonProps {
  rows?: number;
  checkbox?: boolean;
  thumbnail?: boolean;
  trailing?: boolean;
  lines?: 1 | 2;
  flush?: boolean;
}

export const ListSkeleton = ({
  rows = 6,
  checkbox = false,
  thumbnail = false,
  trailing = false,
  lines = 2,
  flush = false,
}: ListSkeletonProps) => (
  <SkeletonRegion className={flush ? "wmx-skeleton-list--flush" : undefined}>
    {range(rows).map((r) => (
      <div key={r} className="wmx-skeleton-list__row">
        {checkbox && <Skeleton width={16} height={16} radius={3} />}
        {thumbnail && <Skeleton width={40} height={40} />}
        <div className="wmx-skeleton-list__text">
          <Skeleton width={`${cycle(r * 3)}%`} className={lines > 1 ? "mb-2" : undefined} />
          {lines > 1 && <Skeleton width={`${cycle(r * 3 + 1) / 2}%`} height={10} />}
        </div>
        {trailing && <Skeleton width={64} height={24} />}
      </div>
    ))}
  </SkeletonRegion>
);

// ─── Form ─────────────────────────────────────────────────────────────────────
interface FormSkeletonProps {
  fields?: number;
  columns?: 1 | 2;
  actions?: boolean;
}

export const FormSkeleton = ({ fields = 4, columns = 1, actions = true }: FormSkeletonProps) => (
  <SkeletonRegion>
    <div className="row">
      {range(fields).map((f) => (
        <div key={f} className={columns === 2 ? "col-md-6 mb-3" : "col-12 mb-3"}>
          <Skeleton width={`${cycle(f) / 2}%`} className="mb-2" />
          <Skeleton height={38} />
        </div>
      ))}
    </div>
    {actions && (
      <div className="d-flex justify-content-end">
        <Skeleton width={110} height={38} />
      </div>
    )}
  </SkeletonRegion>
);

// ─── Cell grid ────────────────────────────────────────────────────────────────
interface GridSkeletonProps {
  rows?: number;
  cols?: number;
  cellHeight?: number;
  caption?: React.ReactNode;
  stagger?: boolean;
  className?: string;
}

export const GridSkeleton = ({
  rows = 4,
  cols = 14,
  cellHeight = 22,
  caption,
  stagger = false,
  className,
}: GridSkeletonProps) => (
  <SkeletonRegion className={`wmx-skeleton-grid${className ? ` ${className}` : ""}`}>
    <div
      className="wmx-skeleton-grid__cells"
      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
    >
      {range(rows * cols).map((i) => (
        <Skeleton
          key={i}
          height={cellHeight}
          radius={4}
          style={stagger ? { animationDelay: `${(i % cols) * 0.04}s` } : undefined}
        />
      ))}
    </div>
    {caption && <div className="wmx-skeleton-grid__caption">{caption}</div>}
  </SkeletonRegion>
);

// ─── Whole page ───────────────────────────────────────────────────────────────
// tabsLayout="side" mirrors the detail screens' vertical pills (col-2 | col-10).
interface PageSkeletonProps {
  breadcrumb?: boolean;
  tabs?: number;
  tabsLayout?: "top" | "side";
  chart?: boolean;
  rows?: number;
}

export const PageSkeleton = ({
  breadcrumb = true,
  tabs = 0,
  tabsLayout = "top",
  chart = false,
  rows = 10,
}: PageSkeletonProps) => {
  const body = (
    <>
      {chart && (
        <div className="mb-3">
          <ChartSkeleton />
        </div>
      )}
      <TableSkeleton toolbar rows={rows} />
    </>
  );
  return (
    <SkeletonRegion>
      {breadcrumb && (
        <div className="page-breadcums">
          <Skeleton width={180} height={22} className="mb-2" />
          <Skeleton width={240} />
        </div>
      )}
      {tabs > 0 && tabsLayout === "side" ? (
        <div className="row mx-1 pt-3">
          <div className="col-2 px-0">
            <div className="wmx-skeleton-rail">
              {range(tabs).map((t) => (
                <div key={t} className="wmx-skeleton-rail__item">
                  <Skeleton width={`${cycle(t * 3 + 1)}%`} />
                </div>
              ))}
            </div>
          </div>
          <div className="col-10">{body}</div>
        </div>
      ) : (
        <>
          {tabs > 0 && (
            <div className="d-flex gap-2 mb-3">
              {range(tabs).map((t) => (
                <Skeleton key={t} width={96} height={34} radius={17} />
              ))}
            </div>
          )}
          {body}
        </>
      )}
    </SkeletonRegion>
  );
};
